import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { TABLE_ICONS } from '../utils/tableIcons';
import { clearAuthToken } from '../utils/auth';
import { exportBirthdaysToExcel } from '../utils/exportToExcel';
import './Birthdays.css';
import '../components/SubscriptionFilters.css';

const Birthdays = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBirthdays, setUserBirthdays] = useState([]);
  const [showBirthdayFullScreen, setShowBirthdayFullScreen] = useState(false);
  const [birthdateFilter, setBirthdateFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleLogout = () => {
    clearAuthToken();
    navigate('/admin/login');
  };

  useEffect(() => {
    fetchBirthdays();
  }, []);

  const fetchBirthdays = async () => {
    try {
      setLoading(true);

      // Test Firebase connection first

      // Fetch users collection first
      
      const usersSnapshot = await getDocs(collection(db, 'users'));

      // Fetch birthdays collection (phone number documents)
      
      const birthdaysSnapshot = await getDocs(collection(db, 'birthdays'));

      // Create a map of birthdays by user phone number
      const birthdaysMap = new Map();
      
      // Process each phone number document in birthdays collection
      birthdaysSnapshot.forEach((phoneDoc) => {
        const phoneNumber = phoneDoc.id; // Document ID is the phone number
        const phoneDocData = phoneDoc.data(); // Get the document data

        const birthdaysForThisPhone = [];
        
        // Each field in the document is a birthday entry (map/object)
        Object.keys(phoneDocData).forEach((birthdayId) => {
          const birthdayData = phoneDocData[birthdayId];
          
          // Check if this field is a birthday object (has required fields)
          if (birthdayData && typeof birthdayData === 'object' && birthdayData.name) {

            birthdaysForThisPhone.push({
              id: birthdayId,
              name: birthdayData.name,
              birthDate: birthdayData.birthDate ? new Date(birthdayData.birthDate.seconds * 1000) : null,
              createdAt: birthdayData.createdAt ? new Date(birthdayData.createdAt.seconds * 1000) : null,
              updatedAt: birthdayData.updatedAt ? new Date(birthdayData.updatedAt.seconds * 1000) : null,
              notes: birthdayData.notes || '',
              phoneNumber: birthdayData.phoneNumber || phoneNumber
            });
          }
        });

        if (birthdaysForThisPhone.length > 0) {
          birthdaysMap.set(phoneNumber, birthdaysForThisPhone);
          birthdaysMap.set(String(phoneNumber), birthdaysForThisPhone);
          // Also try number format
          birthdaysMap.set(parseInt(phoneNumber), birthdaysForThisPhone);
        }
      });

      // Create users list with their birthdays
      const usersList = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();

        // Look for birthdays for this user
        const userBirthdays = birthdaysMap.get(userData.mobileNumber) || 
                             birthdaysMap.get(String(userData.mobileNumber)) || [];

        // Format user's own birthdate if available - check multiple possible field names
        let formattedBirthdate = 'N/A';
        const possibleBirthdateFields = ['birthdate', 'dateOfBirth', 'dob', 'birthday', 'birthDate'];
        
        for (const field of possibleBirthdateFields) {
          if (userData[field]) {
            if (userData[field].seconds) {
              // Firestore timestamp
              formattedBirthdate = new Date(userData[field].seconds * 1000).toLocaleDateString('en-IN');
              break;
            } else if (typeof userData[field] === 'string' && userData[field] !== '') {
              // String date
              const date = new Date(userData[field]);
              if (!isNaN(date.getTime())) {
                formattedBirthdate = date.toLocaleDateString('en-IN');
                break;
              }
            } else if (userData[field] instanceof Date) {
              // Date object
              formattedBirthdate = userData[field].toLocaleDateString('en-IN');
              break;
            }
          }
        }
        
        // Debug log to see what fields are available

        // Only add user to list if they have birthdays added
        if (userBirthdays.length > 0) {
          
          usersList.push({
            id: doc.id,
            name: userData.name || 'Unknown',
            phoneNumber: userData.mobileNumber || 'N/A',
            address: userData.address || 'N/A',
            pincode: userData.pincode || 'N/A',
            birthdate: formattedBirthdate,
            birthdays: userBirthdays,
            totalBirthdays: userBirthdays.length,
            createdAt: userData.createdAt ? new Date(userData.createdAt.seconds * 1000) : null
          });
        } else {
          
        }
      });

      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setUserBirthdays(user.birthdays);
    setShowBirthdayFullScreen(true);
  };

  const closeBirthdayFullScreen = () => {
    setShowBirthdayFullScreen(false);
    setSelectedUser(null);
    setUserBirthdays([]);
  };

  const formatDate = (date) => {
    if (!date) return 'Not provided';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateDDMMYYYY = (date) => {
    if (!date) return 'Unknown';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getAge = (birthDate) => {
    if (!birthDate) return 'Unknown';
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  // Filter users based on search term and birthdate
  const filteredUsers = users.filter(user => {
    const nameStr = String(user.name || '').toLowerCase();
    const phoneStr = String(user.phoneNumber || '');
    
    const matchesSearch = nameStr.includes(searchTerm.toLowerCase()) ||
                          phoneStr.includes(searchTerm);
    
    let matchesBirthdate = true;
    
    // Debug logging

    if (birthdateFilter === 'range' && startDate && endDate) {
      // Parse user's birthdate for filtering - handle DD-MM-YYYY format
      const userBirthdate = user.birthdate && user.birthdate !== 'N/A' 
        ? new Date(user.birthdate.split('-').reverse().join('-')) 
        : null;

      if (userBirthdate && !isNaN(userBirthdate.getTime())) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        // For date range, we need to consider month and day only (ignore year)
        const userMonthDay = new Date(2000, userBirthdate.getMonth(), userBirthdate.getDate());
        const startMonthDay = new Date(2000, start.getMonth(), start.getDate());
        const endMonthDay = new Date(2000, end.getMonth(), end.getDate());
        
        matchesBirthdate = userMonthDay >= startMonthDay && userMonthDay <= endMonthDay;
        
      } else {
        matchesBirthdate = false;
        
      }
    } else if (birthdateFilter === 'today') {
      const today = new Date();
      const todayStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
      matchesBirthdate = user.birthdate === todayStr;
      
    } else if (birthdateFilter === 'week') {
      const today = new Date();
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      const userBirthdate = user.birthdate && user.birthdate !== 'N/A' 
        ? new Date(user.birthdate.split('-').reverse().join('-')) 
        : null;

      if (userBirthdate && !isNaN(userBirthdate.getTime())) {
        // Check if birthday is in next 7 days (ignoring year)
        const currentYear = today.getFullYear();
        const userBirthdayThisYear = new Date(currentYear, userBirthdate.getMonth(), userBirthdate.getDate());
        const userBirthdayNextYear = new Date(currentYear + 1, userBirthdate.getMonth(), userBirthdate.getDate());
        
        matchesBirthdate = (userBirthdayThisYear >= today && userBirthdayThisYear <= weekFromNow) ||
                           (userBirthdayNextYear >= today && userBirthdayNextYear <= weekFromNow);
        
      } else {
        matchesBirthdate = false;
        
      }
    } else if (birthdateFilter === 'month') {
      const today = new Date();
      const currentMonth = today.getMonth();
      
      const userBirthdate = user.birthdate && user.birthdate !== 'N/A' 
        ? new Date(user.birthdate.split('-').reverse().join('-')) 
        : null;

      if (userBirthdate && !isNaN(userBirthdate.getTime())) {
        matchesBirthdate = userBirthdate.getMonth() === currentMonth;
        
      } else {
        matchesBirthdate = false;
        
      }
    }

    return matchesSearch && matchesBirthdate;
  });

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="birthdays-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="birthdays-page">
      <div className="page-header birthdays-header">
        <h2>Users with Birthdays</h2>
        <div className="birthdays-header-actions">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search users by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* Filter row above the table */}
      <div className="subscription-filters-row" style={{ marginLeft: 0, marginTop: 0 }}>
        <label className="subscription-filter-label-inline">Birthdate:
          <select
            className="subscription-filter-select"
            value={birthdateFilter}
            onChange={e => setBirthdateFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="today">Today</option>
            <option value="week">Next 7 Days</option>
            <option value="month">This Month</option>
            <option value="range">Date Range</option>
          </select>
        </label>
        {birthdateFilter === 'range' && (
          <>
            <label className="subscription-filter-label-inline">From:
              <input
                type="date"
                className="subscription-filter-select"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{ maxWidth: 150 }}
              />
            </label>
            <label className="subscription-filter-label-inline">To:
              <input
                type="date"
                className="subscription-filter-select"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{ maxWidth: 150 }}
              />
            </label>
          </>
        )}
        <button
          className="subscription-export-btn"
          onClick={() => exportBirthdaysToExcel(filteredUsers, 'birthdays.xlsx')}
          style={{ marginLeft: 'auto' }}
        >
          Export
        </button>
      </div>

      {/* Users List */}
      <div className="users-section">
        <div className="birthdays-content">
          {currentUsers.length > 0 ? (
            <>
              <div className="birthdays-table">
                <table>
                  <thead>
                    <tr>
                      <th>Sr. No</th>
                      <th>Name</th>
                      <th>Mobile Number</th>
                      <th>Address</th>
                      <th>Pincode</th>
                      <th>Birthdate</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUsers.map((user, index) => (
                      <tr key={user.id}>
                        <td>{indexOfFirstUser + index + 1}</td>
                        <td>
                          <span className="name">{user.name}</span>
                        </td>
                        <td>
                          <span className="phone-number">{user.phoneNumber}</span>
                        </td>
                        <td>
                          <span className="address">{user.address}</span>
                        </td>
                        <td>
                          <span className="pincode">{user.pincode}</span>
                        </td>
                        <td>
                          <span className="birthdate">{user.birthdate}</span>
                        </td>
                        <td>
                          <button
                            className="eye-btn"
                            onClick={() => handleUserClick(user)}
                            title="View Birthdays"
                          >
                            <span className="eye-icon">{TABLE_ICONS.VIEW}</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => paginate(currentPage - 1)} 
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => paginate(index + 1)}
                      className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  
                  <button 
                    onClick={() => paginate(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-data">
              <div className="no-data-icon">👥</div>
              <h3>No Users Found</h3>
              <p>No users with birthday submissions found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Birthday Full Screen View */}
      {showBirthdayFullScreen && selectedUser && (
        <div className="fullscreen-overlay">
          <div className="fullscreen-content">
            {/* Admin Panel Header */}
            <div className="admin-panel-header">
              <h1>Welcome to Admin Panel</h1>
              <div className="admin-user-info">
                <span>Admin User</span>
                <button onClick={handleLogout} className="logout-btn">
                  Logout
                </button>
              </div>
            </div>
            
            <div className="fullscreen-header">
              <div className="header-content">
                <h2>Birthdays for {selectedUser.name}</h2>
                <div className="birthday-count-badge">
                  Total Birthdays Added: {selectedUser.totalBirthdays}
                </div>
              </div>
              <button className="back-to-list-btn" onClick={closeBirthdayFullScreen}>
                ← Back to Users List
              </button>
            </div>
            
            <div className="fullscreen-body">

              <div className="birthdays-content">
                {userBirthdays.length > 0 ? (
                  <div className="birthdays-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Sr. No</th>
                          <th>Name</th>
                          <th>Birth Date</th>
                          <th>Age</th>
                          <th>Notes</th>
                          <th>Added On</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userBirthdays.map((birthday, index) => (
                          <tr key={birthday.id}>
                            <td>{index + 1}</td>
                            <td>
                              <span className="name">{birthday.name}</span>
                            </td>
                            <td>
                              <span className="date">{formatDate(birthday.birthDate)}</span>
                            </td>
                            <td>
                              <span className="age">{getAge(birthday.birthDate)} years</span>
                            </td>
                            <td>
                              <span className="notes">{birthday.notes || '-'}</span>
                            </td>
                            <td>
                              <span className="date">
                                {birthday.createdAt ? formatDateDDMMYYYY(birthday.createdAt) : 'Unknown'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data">
                    <div className="no-data-icon">🎂</div>
                    <h3>No Birthdays Found</h3>
                    <p>This user hasn't added any birthdays yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Birthdays;